
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Video {
  id: string;
  url: string;
  title: string;
  createdAt: string;
}

export const useVideoManagement = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .storage
        .from('videos')
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      if (data) {
        const videos = await Promise.all(data.map(async (file) => {
          const { data: publicUrlData } = supabase
            .storage
            .from('videos')
            .getPublicUrl(file.name);

          return {
            id: file.id,
            url: publicUrlData.publicUrl,
            title: file.metadata?.title || file.name.split('.')[0],
            createdAt: file.created_at,
          };
        }));

        setVideos(videos);
      }
    } catch (error: any) {
      console.error("Error loading videos:", error);
      toast({
        variant: "destructive",
        title: "Failed to load videos",
        description: error.message || "An error occurred while loading videos.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId: string, filePath: string) => {
    try {
      const { error } = await supabase
        .storage
        .from('videos')
        .remove([filePath]);

      if (error) throw error;

      setVideos(videos.filter(video => video.id !== videoId));
      toast({
        title: "Video deleted",
        description: "The video has been deleted successfully.",
      });
    } catch (error: any) {
      console.error("Error deleting video:", error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "An error occurred while deleting the video.",
      });
    }
  };

  const handleUploadComplete = (videoUrl: string, videoTitle: string) => {
    const newVideo = {
      id: Date.now().toString(),
      url: videoUrl,
      title: videoTitle,
      createdAt: new Date().toISOString(),
    };
    setVideos([newVideo, ...videos]);
  };

  const handleAnalyzeVideo = (video: Video) => {
    setSelectedVideo(video);
    setShowAnalysisDialog(true);
  };

  // Extract filename from URL for delete operation
  const getFilenameFromUrl = (url: string) => {
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  };

  return {
    videos,
    loading,
    showUploadDialog,
    setShowUploadDialog,
    showAnalysisDialog,
    setShowAnalysisDialog,
    selectedVideo,
    handleDeleteVideo,
    handleUploadComplete,
    handleAnalyzeVideo,
    getFilenameFromUrl,
  };
};
